import React from 'react'
import {
  Breakpoint,
  BREAKPOINT_ORDER,
  BREAKPOINT_QUERIES,
  LANDSCAPE_QUERY,
  TOUCH_QUERY
} from 'shared/constants/breakpoints'

export interface IBreakpointState {
  // Текущий тип экрана — один из восьми, их разбор в constants/breakpoints.ts
  breakpoint: Breakpoint
  // Палец, а не мышь: `hover: none` и `pointer: coarse` разом
  isTouch: boolean
  isLandscape: boolean
  // Телефон в портрете или окно такой же ширины
  isPhone: boolean
  isPhoneLandscape: boolean
  isTablet: boolean
  isTabletLandscape: boolean
  // Любой сенсорный экран боком: и телефон, и планшет
  isTouchLandscape: boolean
  // Мышь в окне уже 1024px
  isCompactWindow: boolean
  // Мышь от 1024px: ноутбук, монитор, Full HD
  isDesktop: boolean
}

const resolveBreakpoint = (): Breakpoint =>
  BREAKPOINT_ORDER.find((item) => window.matchMedia(BREAKPOINT_QUERIES[item]).matches) ?? 'desktop'

const getState = (): IBreakpointState => {
  const breakpoint = resolveBreakpoint()
  const isTouch = window.matchMedia(TOUCH_QUERY).matches
  const isPhone = breakpoint === 'phone'
  const isPhoneLandscape = breakpoint === 'phone-landscape'
  const isTabletLandscape = breakpoint === 'tablet-landscape'

  return {
    breakpoint,
    isTouch,
    isLandscape: window.matchMedia(LANDSCAPE_QUERY).matches,
    isPhone,
    isPhoneLandscape,
    isTablet: breakpoint === 'tablet',
    isTabletLandscape,
    isTouchLandscape: isPhoneLandscape || isTabletLandscape,
    isCompactWindow: breakpoint === 'compact',
    isDesktop: breakpoint === 'desktop'
  }
}

const isSameState = (a: IBreakpointState, b: IBreakpointState) =>
  a.breakpoint === b.breakpoint && a.isTouch === b.isTouch && a.isLandscape === b.isLandscape

// Один источник правды о размере экрана: matchMedia, а не window.innerWidth,
// чтобы JS переключал раскладку ровно там же, где медиазапросы в стилях
export const useBreakpoint = (): IBreakpointState => {
  const [state, setState] = React.useState<IBreakpointState>(getState)

  React.useEffect(() => {
    const update = () => setState((prev) => {
      const next = getState()

      return isSameState(prev, next) ? prev : next
    })

    const mediaQueries = [
      ...Object.values(BREAKPOINT_QUERIES),
      TOUCH_QUERY,
      LANDSCAPE_QUERY
    ].map((query) => window.matchMedia(query))

    update()
    mediaQueries.forEach((mediaQuery) => mediaQuery.addEventListener('change', update))

    return () => {
      mediaQueries.forEach((mediaQuery) => mediaQuery.removeEventListener('change', update))
    }
  }, [])

  return state
}
